/**
 * Control guides — V117-3-5 (ADR-0112 §6–§7; D-116-7, D-116-13, D-117-11).
 *
 * Layer 2 of the remediation-context contract: versioned, reviewed content (control_guides, migration 1020), one
 * ACTIVE version per control, written only through the review queue (`proposed_changes` target `control_guide`,
 * widened by 1019). Agents draft guides; the owner approves them in the same batch as the control.
 *
 *   validateGuideState     the apply-time (and approve-time) gate: intent; every `machine` acceptance criterion names
 *                          an existing layer-1 content check of the control's active spec (`check_ref`, the stable keys
 *                          of content-check-service); citations reference a source by (source_kind, source_id,
 *                          doc_id?) with a pinpoint and an excerpt >= 40 chars; when Track 1's `source_documents`
 *                          table exists the source must exist there (and the excerpt must appear in its stored text,
 *                          when the row carries one) — tolerated when the table is absent
 *   applyControlGuideChange  the queue writer: create / update = a new version (the previous one superseded in the same
 *                          batch); deprecate = the active version superseded
 *   getActiveGuide / serveGuide  read for the brief, MCP and the route: variants filtered by the workspace's roles,
 *                          system classes and active law parameters; citations whose source is vacated or annulled are
 *                          hidden (when the source table carries `legal_status`) and counted for a guide revision.
 * The guide text is platform content, the same for every workspace; only the variant filter is workspace-specific.
 */
import { z } from 'zod';
import { canonicalJson, generateRandomId, sha256Hex } from '../utils/audit-crypto.js';
import { contentCheckKeys, parseContentChecks } from './content-check-service.js';
import { ApplyDeferredError } from './framework-requirement-service.js';
import { getStatuteTextRecord } from './statute-text-service.js';
export const MIN_CITATION_EXCERPT = 40;
export const GUIDE_ROLES = ['developer', 'deployer', 'operator'];
const text = (max) => z.string().trim().min(1).max(max);
const oneOrMany = (t) => z.union([t, z.array(t).min(1).max(20)]);
const criterionSchema = z.object({
    text: text(1000),
    kind: z.enum(['machine', 'human']),
    check_ref: z.string().max(100).optional(),
}).strict();
const exampleSchema = z.object({ artifact: text(500), why: text(1000) }).strict();
const variantSchema = z.object({
    key: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
    role: oneOrMany(z.enum(GUIDE_ROLES)).optional(),
    system_class: oneOrMany(z.string().regex(/^[a-z][a-z0-9_]{0,63}$/)).optional(),
    law_parameter: z.object({ key: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/), value: z.union([z.string(), z.number(), z.boolean()]) }).strict().optional(),
    guidance: text(4000),
}).strict();
const citationSchema = z.object({
    source_kind: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
    source_id: text(200),
    doc_id: text(200).optional(),
    pinpoint: text(300),
    excerpt: z.string().trim().min(MIN_CITATION_EXCERPT, `excerpt must be at least ${MIN_CITATION_EXCERPT} characters (ADR-0076 grounding)`).max(4000),
    /**
     * Set by the validator, never trusted from the proposal: true when no stored source text exists to ground the
     * excerpt against (ADR-0076). Accepted, stored on the guide and shown in the queue view so a reviewer knows.
     */
    ungrounded: z.boolean().optional(),
}).strict();
export const guideContentSchema = z.object({
    intent: text(4000),
    acceptance_criteria: z.array(criterionSchema).min(1).max(50),
    evidence_examples: z.array(exampleSchema).max(50).default([]),
    variants: z.array(variantSchema).max(50).default([]),
    common_mistakes: z.array(text(1000)).max(50).default([]),
    citations: z.array(citationSchema).max(100).default([]),
}).strict();
/**
 * A deferral (ApplyDeferredError): a bad guide stays PENDING with the reason recorded (impact.apply_blocked) and a
 * batch approval goes on with the rest of the batch (review #464, H1).
 */
export class InvalidControlGuideError extends ApplyDeferredError {
    controlId;
    errors;
    constructor(controlId, errors) {
        super(`Control guide for ${controlId} is invalid: ${errors.join('; ')}`);
        this.controlId = controlId;
        this.errors = errors;
        this.name = 'InvalidControlGuideError';
    }
}
const parseJsonField = (v) => {
    if (typeof v !== 'string')
        return v;
    try {
        return JSON.parse(v);
    }
    catch {
        return v;
    }
};
/** Read a proposal's state (either `*_json` columns or plain keys) into guide content. */
export function guideContentFromState(state) {
    const pick = (k) => (state[k] !== undefined ? parseJsonField(state[k]) : parseJsonField(state[`${k}_json`]));
    const raw = {
        intent: state.intent,
        acceptance_criteria: pick('acceptance_criteria'),
        evidence_examples: pick('evidence_examples') ?? [],
        variants: pick('variants') ?? [],
        common_mistakes: pick('common_mistakes') ?? [],
        citations: pick('citations') ?? [],
    };
    const r = guideContentSchema.safeParse(raw);
    if (!r.success)
        return { content: null, errors: r.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`) };
    return { content: r.data, errors: [] };
}
const collapse = (s) => s.replace(/\s+/g, ' ').trim();
const TEXT_COLUMNS = ['text', 'normalized_text', 'extracted_text', 'content_text', 'body_text'];
const missingTable = (err) => /no such table|does not exist/i.test(err instanceof Error ? err.message : String(err));
const errText = (err) => (err instanceof Error ? err.message : String(err));
/** The stored text a citation can be grounded against, or null when none is stored. Throws on a query error. */
async function storedSourceText(db, cit, docRow, opts) {
    const fromRow = docRow ? TEXT_COLUMNS.map((k) => docRow[k]).find((v) => typeof v === 'string' && v.length > 0) : undefined;
    if (fromRow)
        return fromRow;
    if (cit.source_kind !== 'legislation' || !opts.statuteStore)
        return null;
    let rec;
    try {
        rec = await getStatuteTextRecord(db, cit.source_id);
    }
    catch (err) {
        if (missingTable(err))
            return null;
        throw err;
    }
    if (!rec || rec.status !== 'stored' || !rec.content_sha256)
        return null;
    return opts.statuteStore.get(rec.content_sha256);
}
/**
 * Check guide content for a control: machine criteria against the active spec's content checks, variant keys,
 * and every citation — its source must exist in Track 1's `source_documents` (tolerated only where that table does
 * not exist on this database), and its excerpt must appear in the stored source text where one exists (the
 * document row's text, or for legislation the stored statute text). Without stored text the citation is accepted and
 * marked `ungrounded: true`. A query error is a validation error with its message, never swallowed.
 */
export async function checkGuide(db, controlId, content, opts = {}) {
    const errors = [];
    const ctl = await db.query(`SELECT control_id FROM controls WHERE control_id = ?`, [controlId]);
    if (!ctl.results[0])
        errors.push(`control ${controlId} does not exist`);
    let keys = [];
    try {
        const spec = await db.query(`SELECT content_checks_json FROM control_specs WHERE control_id = ? AND superseded_at IS NULL ORDER BY created_at DESC LIMIT 1`, [controlId]);
        keys = contentCheckKeys(parseContentChecks(spec.results[0]?.content_checks_json ?? '{}'));
    }
    catch (err) {
        errors.push(`the active spec's content checks could not be read: ${errText(err)}`);
    }
    keys = [...new Set([...keys, ...(opts.pendingSpecCheckKeys ?? [])])];
    content.acceptance_criteria.forEach((c, i) => {
        if (c.kind === 'machine') {
            if (!c.check_ref)
                errors.push(`acceptance_criteria.${i}: a machine criterion must name the content check that decides it (check_ref)`);
            else if (!keys.includes(c.check_ref))
                errors.push(`acceptance_criteria.${i}: check_ref '${c.check_ref}' is not a content check of ${controlId}'s active spec (${keys.length ? keys.join(', ') : 'it declares none'})`);
        }
    });
    const variantKeys = new Set();
    for (const v of content.variants) {
        if (variantKeys.has(v.key))
            errors.push(`variants: duplicate key ${v.key}`);
        variantKeys.add(v.key);
    }
    const citations = [];
    let docsTable = true;
    for (const [i, raw] of content.citations.entries()) {
        const { ungrounded: _claimed, ...cit } = raw;
        let row = null;
        if (docsTable) {
            try {
                row = (await db.query(cit.doc_id
                    ? `SELECT * FROM source_documents WHERE doc_id = ? LIMIT 1`
                    : `SELECT * FROM source_documents WHERE source_kind = ? AND source_id = ? LIMIT 1`, cit.doc_id ? [cit.doc_id] : [cit.source_kind, cit.source_id])).results[0] ?? null;
                if (!row) {
                    errors.push(`citations.${i}: source ${cit.source_kind}/${cit.source_id}${cit.doc_id ? ` document ${cit.doc_id}` : ''} not found`);
                    citations.push(cit);
                    continue;
                }
                if (cit.doc_id && (row.source_kind !== cit.source_kind || row.source_id !== cit.source_id)) {
                    errors.push(`citations.${i}: document ${cit.doc_id} belongs to ${String(row.source_kind)}/${String(row.source_id)}, not ${cit.source_kind}/${cit.source_id}`);
                }
            }
            catch (err) {
                if (missingTable(err))
                    docsTable = false; // before migration 1006 on this plane: existence is not checked
                else {
                    errors.push(`citations.${i}: the source could not be checked: ${errText(err)}`);
                    citations.push(cit);
                    continue;
                }
            }
        }
        let text;
        try {
            text = await storedSourceText(db, cit, row, opts);
        }
        catch (err) {
            errors.push(`citations.${i}: the stored source text could not be read: ${errText(err)}`);
            citations.push(cit);
            continue;
        }
        if (text === null) {
            citations.push({ ...cit, ungrounded: true });
            continue;
        }
        if (!collapse(text).includes(collapse(cit.excerpt)))
            errors.push(`citations.${i}: the excerpt does not appear in the stored source text`);
        citations.push(cit);
    }
    return { errors, citations };
}
/** The errors of `checkGuide` (kept for callers that only gate). */
export async function validateGuideState(db, controlId, content, opts = {}) {
    return (await checkGuide(db, controlId, content, opts)).errors;
}
const contentHash = (c) => sha256Hex(canonicalJson(c));
function rowToGuide(r) {
    const arr = (v) => { const p = parseJsonField(v); return Array.isArray(p) ? p : []; };
    return {
        guide_id: String(r.guide_id), control_id: String(r.control_id), version: Number(r.version),
        status: r.status === 'superseded' ? 'superseded' : 'active',
        intent: String(r.intent ?? ''),
        acceptance_criteria: arr(r.acceptance_criteria_json),
        evidence_examples: arr(r.evidence_examples_json),
        variants: arr(r.variants_json),
        common_mistakes: arr(r.common_mistakes_json),
        citations: arr(r.citations_json),
        content_sha256: String(r.content_sha256 ?? ''), source_kind: String(r.source_kind ?? 'admin_edit'),
        reviewer: String(r.reviewer ?? ''), reviewed_at: String(r.reviewed_at ?? ''), change_id: r.change_id == null ? null : String(r.change_id),
    };
}
/** The active guide of a control (control plane), or null — also null before migration 1020 on this plane. */
export async function getActiveGuide(content, controlId) {
    try {
        const r = await content.query(`SELECT * FROM control_guides WHERE control_id = ? AND status = 'active' LIMIT 1`, [controlId]);
        return r.results[0] ? rowToGuide(r.results[0]) : null;
    }
    catch (err) {
        if (missingTable(err))
            return null; // before migration 1020 on this plane
        throw err;
    }
}
/** The proposal sources a guide row accepts (control_guides.source_kind CHECK). */
const GUIDE_SOURCE_KINDS = new Set(['research_pipeline', 'admin_edit', 'seed', 'partner_import', 'agent']);
/**
 * Queue writer for `control_guide` proposals. create / update → a new version (the previous active one superseded in
 * the same batch); deprecate → the active version superseded. Never edits a version in place. Returns the guide id.
 */
export async function applyControlGuideChange(db, change, reviewer, opts = {}) {
    let state = {};
    try {
        state = JSON.parse(change.proposed_state);
    }
    catch {
        throw new InvalidControlGuideError(change.target_id ?? '?', ['proposed_state is not JSON']);
    }
    const controlId = String(state.control_id ?? change.target_id ?? '');
    if (!controlId)
        throw new InvalidControlGuideError('?', ['control_guide proposal missing control_id']);
    if (!reviewer)
        throw new InvalidControlGuideError(controlId, ['a control guide is applied only with a reviewer']);
    const now = new Date().toISOString();
    const active = await getActiveGuide(db, controlId);
    if (change.change_type === 'deprecate') {
        if (!active)
            throw new InvalidControlGuideError(controlId, ['no active guide to deprecate']);
        await db.execute(`UPDATE control_guides SET status = 'superseded', superseded_at = ? WHERE guide_id = ? AND status = 'active'`, [now, active.guide_id]);
        return active.guide_id;
    }
    const { content, errors } = guideContentFromState(state);
    if (!content)
        throw new InvalidControlGuideError(controlId, errors);
    const checked = await checkGuide(db, controlId, content, opts);
    if (checked.errors.length)
        throw new InvalidControlGuideError(controlId, checked.errors);
    const stored = { ...content, citations: checked.citations };
    const sha = await contentHash(stored);
    if (active && active.content_sha256 === sha)
        return active.guide_id; // re-applying the same content is a no-op
    const versions = await db.query(`SELECT MAX(version) AS v FROM control_guides WHERE control_id = ?`, [controlId]);
    const version = Number(versions.results[0]?.v ?? 0) + 1;
    const guideId = generateRandomId('cgd');
    const stmts = [];
    if (active)
        stmts.push({ sql: `UPDATE control_guides SET status = 'superseded', superseded_at = ? WHERE guide_id = ? AND status = 'active'`, params: [now, active.guide_id] });
    stmts.push({
        sql: `INSERT INTO control_guides (guide_id, control_id, version, status, intent, acceptance_criteria_json, evidence_examples_json,
            variants_json, common_mistakes_json, citations_json, content_sha256, source_kind, reviewer, reviewed_at, change_id)
          VALUES (?, ?, ?, 'active', ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        params: [guideId, controlId, version, stored.intent, JSON.stringify(stored.acceptance_criteria), JSON.stringify(stored.evidence_examples),
            JSON.stringify(stored.variants), JSON.stringify(stored.common_mistakes), JSON.stringify(stored.citations), sha,
            GUIDE_SOURCE_KINDS.has(change.source_kind) ? change.source_kind : 'admin_edit', reviewer, change.reviewed_at ?? now, change.change_id],
    });
    await db.batch(stmts);
    return guideId;
}
const asList = (v) => (v === undefined ? [] : Array.isArray(v) ? v : [v]);
/** Does a variant fit the audience? Every selector it declares must match; an unknown audience dimension keeps it. */
export function variantFits(v, a) {
    const roles = asList(v.role);
    if (roles.length && a.roles.length && !roles.some((r) => a.roles.includes(r)))
        return false;
    const classes = asList(v.system_class);
    if (classes.length && a.systemClasses.length && !classes.some((c) => a.systemClasses.includes(c)))
        return false;
    if (v.law_parameter && a.lawParameters) {
        const values = a.lawParameters.get(v.law_parameter.key);
        if (!values || !values.has(String(v.law_parameter.value)))
            return false;
    }
    return true;
}
const HIDDEN_LEGAL_STATUSES = new Set(['vacated', 'annulled']);
const LEGAL_STATUS_TABLES = {
    legislation: { table: 'legislation', id: 'legislation_id' },
    case_law: { table: 'case_law', id: 'case_id' },
};
/** Source ids (per kind) whose legal_status is vacated / annulled; empty where the column does not exist yet. */
export async function vacatedSources(content, citations) {
    const out = new Set();
    for (const [kind, t] of Object.entries(LEGAL_STATUS_TABLES)) {
        const ids = [...new Set(citations.filter((c) => c.source_kind === kind).map((c) => c.source_id))];
        for (const id of ids) {
            try {
                const r = await content.query(`SELECT legal_status FROM ${t.table} WHERE ${t.id} = ?`, [id]);
                const s = r.results[0]?.legal_status;
                if (s && HIDDEN_LEGAL_STATUSES.has(String(s).toLowerCase()))
                    out.add(`${kind}\u0000${id}`);
            }
            catch (err) {
                // No legal_status column (or table) on this plane yet: nothing is hidden. Anything else is an error.
                if (missingTable(err) || /no such column|column .* does not exist/i.test(errText(err)))
                    break;
                throw err;
            }
        }
    }
    return out;
}
/** The guide as served: variants for this audience, vacated / annulled citations hidden and counted. */
export async function serveGuide(content, guide, audience) {
    const hidden = await vacatedSources(content, guide.citations);
    const citations = guide.citations.filter((c) => !hidden.has(`${c.source_kind}\u0000${c.source_id}`));
    return {
        control_id: guide.control_id, version: guide.version, intent: guide.intent,
        acceptance_criteria: guide.acceptance_criteria, evidence_examples: guide.evidence_examples,
        variants: guide.variants.filter((v) => variantFits(v, audience)), common_mistakes: guide.common_mistakes,
        citations, hidden_citations: guide.citations.length - citations.length,
        reviewed_at: guide.reviewed_at, content_sha256: guide.content_sha256,
    };
}
/**
 * The workspace's audience (tenant plane), optionally for one system: roles from the inventory lanes (building →
 * developer + deployer, buying → deployer; any production system → operator), the system classes, and the law
 * parameters of the active obligations.
 */
export async function workspaceGuideAudience(db, workspaceId, systemId) {
    const systems = (await db.query(`SELECT system_id, lane, system_class, deployment_status FROM ai_systems WHERE workspace_id = ? ${systemId ? 'AND system_id = ?' : ''}`, systemId ? [workspaceId, systemId] : [workspaceId])).results;
    const roles = new Set();
    const classes = new Set();
    for (const s of systems) {
        if (s.lane === 'building') {
            roles.add('developer');
            roles.add('deployer');
        }
        if (s.lane === 'buying')
            roles.add('deployer');
        if (s.deployment_status === 'production')
            roles.add('operator');
        if (s.system_class)
            classes.add(s.system_class);
    }
    const lawParameters = new Map();
    try {
        const obs = (await db.query(`SELECT so.obligation_json FROM system_obligations so JOIN ai_systems s ON s.system_id = so.system_id
        WHERE s.workspace_id = ? AND so.superseded_at IS NULL ${systemId ? 'AND so.system_id = ?' : ''}`, systemId ? [workspaceId, systemId] : [workspaceId])).results;
        for (const o of obs) {
            let ob;
            try {
                ob = JSON.parse(o.obligation_json);
            }
            catch {
                continue;
            }
            const sets = [ob.law_parameters, ...(ob.control_parameters && typeof ob.control_parameters === 'object' ? Object.values(ob.control_parameters) : [])];
            for (const lp of sets) {
                if (!lp || typeof lp !== 'object' || Array.isArray(lp))
                    continue;
                for (const [k, v] of Object.entries(lp)) {
                    if (v === null || typeof v === 'object')
                        continue;
                    lawParameters.set(k, (lawParameters.get(k) ?? new Set()).add(String(v)));
                }
            }
        }
    }
    catch (err) {
        if (!missingTable(err))
            throw err; // no obligations table on this plane: no law parameters
    }
    return { roles: [...roles].sort(), systemClasses: [...classes].sort(), lawParameters };
}
/** Load and serve the active guide of a control for a workspace (null when the control has no active guide). */
export async function guideForWorkspace(content, tenant, workspaceId, controlId, systemId) {
    const guide = await getActiveGuide(content, controlId);
    if (!guide)
        return null;
    return serveGuide(content, guide, await workspaceGuideAudience(tenant, workspaceId, systemId));
}
//# sourceMappingURL=control-guide-service.js.map